import { useCustomerAvatar } from "@expert/contentful";
import { getLogger } from "@expert/logging";
import { Button, Group, Stack, Text } from "@mantine/core";
import { type JSX, useState } from "react";
import { useTimer } from "react-timer-hook";
import { PhoneNumber } from "@/common-ui";
import sharedStyles from "@/common-ui/sharedStyles.module.css";
import { useAgentSdk, useSession } from "@/sdk";
import { useCallbackStore } from "@/sdk/sessions/callback.store";
import { useAsyncLoading } from "@/shared-utils";
import classes from "./CallbackCallBar.module.css";
import { CallbackCallBarBody } from "./CallbackCallBarBody";

const logger = getLogger({ module: "CallbackCallBar" });

export function CallbackCallBar(): JSX.Element {
    const session = useSession();
    const agentSdk = useAgentSdk();

    const { callbackState, setCallbackSuccessful } = useCallbackStore();
    const [errorCalling, setErrorCalling] = useState(false);
    const [isCalling, handleCallCustomerBack] = useAsyncLoading(async () => {
        if (!callbackState) {
            throw new Error("CallbackCallBar should not be rendered without a callback state");
        }

        setErrorCalling(false);
        // oxlint-disable-next-line no-unused-vars
        const { callbackMDN, ...rest } = callbackState;
        let tries = 0;
        for (tries = 0; tries < 3; tries++) {
            try {
                await agentSdk.callCustomerBack(callbackState);
                logger.info({ callbackState: rest }, "Callback was successful");
                setCallbackSuccessful();
                return;
            } catch (error) {
                logger.error({ error, callbackState: rest }, "Error calling customer back");
                await new Promise((resolve) => {
                    setTimeout(resolve, 1000);
                });
            }
        }
        setErrorCalling(true);
    });

    const customerAvatarUrl = useCustomerAvatar(session.id);

    if (!callbackState) {
        throw new Error("CallbackCallBar should not be rendered without a callback state");
    }

    const expiryTimestamp = new Date(callbackState.scheduledFor);
    const { totalSeconds, isRunning, start } = useTimer({
        expiryTimestamp,
        autoStart: false,
        onExpire: () => {
            void handleCallCustomerBack();
        },
    });

    if (callbackState.scheduledFor && !isRunning && callbackState.scheduledFor > 0) {
        start();
    } else if (callbackState.scheduledFor === 0) {
        void handleCallCustomerBack();
    }

    const mdnText = callbackState.callbackMDN;

    function retry() {
        void handleCallCustomerBack();
    }

    const isCallingState =
        isCalling ||
        (callbackState.callbackType === "CallbackNow" && (callbackState.callbackDelay === 0 || totalSeconds === 0));

    return (
        <Group p={0}>
            {isCallingState ? (
                <Stack gap="0" p={0}>
                    <Text fw={300} size="xs" className={sharedStyles.animatedEllipses}>
                        Calling
                    </Text>
                    <Text className="fs-mask" fw={700} mr="sm" size="sm">
                        <PhoneNumber mdn={mdnText} />
                    </Text>
                </Stack>
            ) : (
                <>
                    <Group align="center" flex="1">
                        {customerAvatarUrl ? (
                            <img
                                className={classes.profilePicture}
                                src={customerAvatarUrl}
                                alt="customer profile avatar"
                            />
                        ) : null}

                        <CallbackCallBarBody
                            mdnText={mdnText}
                            secondsTillCall={totalSeconds}
                            errorCalling={errorCalling}
                        />
                    </Group>
                    <Group gap="sm" justify="flex-end" grow={false} flex="0">
                        {errorCalling && (
                            <Button size="xs" color="primary" onClick={retry}>
                                Retry
                            </Button>
                        )}
                    </Group>
                </>
            )}
        </Group>
    );
}
